<template>
  <div>
    <div class="heading flex items-center justify-between mb-4 pt-4 mx-2">
      <div class="flex item-center">
        <LayersIcon class="w-10 mr-3" />
        <div>
          <h3 class="text-lg text-left text-primary leading-snug font-semibold">
            {{ $t('order.step3') }}
          </h3>
          <h4 class="text-base text-black-display leading-snug">
            {{ $t('order.step3Description') }}
          </h4>
        </div>
      </div>
      <p
        v-if="dirty && error"
        class="text-left text-sm pl-2 text-red-600"
      >
        {{ error }}
      </p>
    </div>
    <div class="flex justify-between items-end pb-2 mx-2">
      <div>
        <p class="text-left pl-1">
          <b class="font-semibold mt-5 text-base md:text-md">{{ categoryName }} - {{ variations }} {{ $t('order.anyConfig') }} </b>
        </p>
        <p class="text-left text-sm pl-2">
          ({{ $t('order.maximum') }} {{ max }} {{ $t('order.inPackage') }})
        </p>
      </div>
      <div>
        <div class="flex justify-center flex-wrap">
          <p class="w-32 leading-none">
            <span class="text-xs">{{ $t('order.flavorsLeft') }}:</span> <span class="text-xs font-bold">{{ variations - usedVariations }}</span>
          </p>
        </div>
        <div class="sum flex justify-center w-32 md:w-42">
          <span class="font-semibold">{{ sum }}</span><span class="mx-1">/</span><span class="text-primary font-semibold">{{ max }}</span>
        </div>
      </div>
    </div>
    <div class="flex custom-scroll px-2">
      <ul class="w-full text-left">
        <li
          v-for="(taste, index) in tastes"
          :key="`taste-${index}`"
          class="flex justify-between my-2 md:my-3"
        >
          <span class="rounded w-full px-3 md:px-6 py-2 md:py-3 mr-1 md:mr-4 text-sm md:text-base border border-gray-100 bg-white">{{ name(taste) }}</span>
          <BaseCounter
            :blocked="blockedCounters"
            :value="flavourNumbers[index]"
            :max="max"
            :sum="sum"
            class="ml-2 md:ml-4"
            @change="updateFlavourNumbers(index, $event)"
          />
        </li>
      </ul>
    </div>
    <div class="sum flex flex-col items-center">
      <h3 class="text-primary font-semibold text-2xl mt-8">
        {{ $t('summary.estimatedPrice') }}
      </h3>
      <p class="text-black-display font-semibold text-5xl">
        {{ orderPrice }} {{ currency }}
      </p>
    </div>
    <div class="flex pt-8 px-3 w-full lg:w-4/5">
      <div class="w-5 h-5 mt-1 mr-2">
        <InfoIcon />
      </div>
      <p
        class="text-xs sm:text-sm text-left leading-loose"
        v-html="categoryDesc"
      />
    </div>
    <BaseTable :pallet="pallet" />
    <div class="heading flex justify-between items-center mb-4 pt-4 px-2">
      <div class="flex items-center">
        <StarIcon class="w-10 mr-3" />
        <div>
          <h3
            class="text-lg text-left text-primary leading-snug"
            v-html="$t('order.step4')"
          />
          <h4 class="text-base text-black-display leading-snug">
            {{ $t('order.step4Description') }}
          </h4>
        </div>
      </div>
      <button
        class="btn btn-outline px-12 py-4 animate"
        @click="toggleStep(3, 4)"
      >
        {{ btnText }}
      </button>
    </div>
  </div>
</template>
<script>
import { mapState, mapMutations, mapGetters } from 'vuex';
import BaseCounter from '@/components/BaseCounter';
import BaseTable from '@/components/BaseTable';
import LayersIcon from '@/assets/icons/layers.svg';
import StarIcon from '@/assets/icons/star.svg';
import InfoIcon from '@/assets/icons/info.svg';
import name from '@/mixins/name';

export default {
  name: 'Step3',
  components: {
    LayersIcon,
    StarIcon,
    InfoIcon,
    BaseCounter,
    BaseTable,
  },
  mixins: [name],
  props: {
    activeStep: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      flavourNumbers: [],
    };
  },
  computed: {
    ...mapState('order', ['subcategory_id', 'error', 'dirty', 'subcategories', 'bonuses', 'extras', 'tastes', 'sum', 'europln']),
    ...mapGetters('order', ['chosenCategoryObj']),
    ...mapGetters('pref', ['getLang']),
    orderPrice() {
      let base = 0;
      let bonusPrice = 0;
      if (this.chosenCategoryObj) {
        base += this.chosenCategoryObj.price_pln;
        Object.keys(this.extras).forEach((el) => {
          if (this.extras[el]) {
            bonusPrice += this.bonuses[el].price_pln;
          }
        });

        return this.convertCurrency(this.sum, bonusPrice, base);
      }

      return 0;
    },
    sum() {
      const arrSum = this.flavourNumbers.reduce((a, b) => Number(a) + Number(b), 0);
      this.setSum(arrSum);
      if (arrSum === this.chosenCategoryObj.quantity_in_package) {
        this.setError(null);
      } else {
        this.setError(this.$t('order.numberError'));
      }

      return arrSum;
    },
    usedVariations() {
      let used = 0;
      this.flavourNumbers.forEach((el) => {
        if (el > 0) used += 1;
      });

      return used;
    },
    btnText() {
      return this.activeStep === 3 ? this.$t('order.expand') : this.$t('order.close');
    },
    tastes() {
      return this.chosenCategoryObj.tastes;
    },
    categoryName() {
      return this.name(this.chosenCategoryObj);
    },
    blockedCounters() {
      return (this.variations - this.usedVariations) === 0 || this.sum === this.max;
    },
    max() {
      return this.chosenCategoryObj.quantity_in_package;
    },
    pallet() {
      return this.getLang === 'pl' ? this.chosenCategoryObj.pallet_pl_json : this.chosenCategoryObj.pallet_en_json;
    },
    categoryDesc() {
      return this.getLang === 'pl' ? this.chosenCategoryObj.description_pl : this.chosenCategoryObj.description_en;
    },
    variations() {
      return this.chosenCategoryObj.allowed_variations;
    },
    currency() {
      return this.getLang === 'en' ? 'euro' : 'zł';
    },
  },
  watch: {
    subcategory_id() {
      this.flavourNumbers = Array(this.tastes.length).fill(0);
    },
  },
  mounted() {
    this.flavourNumbers = Array(this.tastes.length).fill(0);
    this.setDirty(null);
  },
  methods: {
    ...mapMutations('order', ['updateOrder', 'setSum', 'setError', 'setDirty']),
    updateFlavourNumbers(index, val) {
      this.flavourNumbers.splice(index, 1, val);
      this.updateOrder(
        this.flavourNumbers
          .map((el, ind) => ({
            quantity: el,
            taste_id: this.tastes[ind].id,
          }))
          .filter((el) => el.quantity > 0),
      );
    },
    toggleStep(from, to) {
      if (this.activeStep === from) this.$emit('changeStep', to);
      else this.$emit('changeStep', from);
    },
    convertCurrency(sum, bonusPrice, base) {
      return this.getLang === 'en' ? (((sum + bonusPrice) * base) / this.europln).toFixed(2) : ((sum + bonusPrice) * base).toFixed(2);
    },
  },
};
</script>
<style lang="scss">
  .custom-scroll {
    max-height: 500px;
    overflow-y: auto;
    /* width */
    &::-webkit-scrollbar {
      width: 3px;
    }

    /* Track */
    &::-webkit-scrollbar-track {
      background: transparent;
    }

    /* Handle */
    &::-webkit-scrollbar-thumb {
      @apply bg-purple-gray-darker;
    }

    /* Handle on hover */
    &::-webkit-scrollbar-thumb:hover {
      @apply bg-purple-900;
    }
  }
</style>
